import { withNormalizedStatus } from '~/lib/ai/status';
import { aiApi } from '~/modules/query/ai';
import type {
  ArchiveDigitalAssistantVersionRequest,
  ArchiveDigitalAssistantVersionResponse,
  CreateDigitalAssistantRequest,
  CreateDigitalAssistantResponse,
  DeleteDigitalAssistantResponse,
  DuplicateDigitalAssistantRequest,
  DuplicateDigitalAssistantResponse,
  ExecuteDigitalAssistantRequest,
  ExecuteDigitalAssistantResponse,
  ExecuteListingPayloadRequest,
  ExecuteListingPayloadResponse,
  GetDigitalAssistantResponse,
  GetDigitalAssistantVersionRequest,
  GetDigitalAssistantVersionResponse,
  ListDigitalAssistantVersionsResponse,
  ListDigitalAssistantsResponse,
  RestoreDigitalAssistantVersionRequest,
  RestoreDigitalAssistantVersionResponse,
  UpdateDigitalAssistantRequest,
  UpdateDigitalAssistantResponse,
} from '~/modules/query/ai/types';

const seg = encodeURIComponent;
const BASE = '/manager/digital-assistants';

/** D-24: an assistant's or version's status arrives in either case; the UI reads lowercase. */
const normalizeOne = <T extends { data: { status: string } }>(response: T): T => ({
  ...response,
  data: withNormalizedStatus(response.data),
});
const normalizeMany = <T extends { data: { status: string }[] }>(response: T): T => ({
  ...response,
  data: response.data.map(withNormalizedStatus),
});

export const digitalAssistantsApi = aiApi.injectEndpoints({
  endpoints: (builder) => ({
    listDigitalAssistants: builder.query<ListDigitalAssistantsResponse, void>({
      query: () => ({ url: BASE, method: 'GET' }),
      transformResponse: normalizeMany<ListDigitalAssistantsResponse>,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((assistant) => ({
                type: 'AiAssistant' as const,
                id: assistant.id,
              })),
              { type: 'AiAssistant' as const, id: 'LIST' },
            ]
          : [{ type: 'AiAssistant' as const, id: 'LIST' }],
    }),
    createDigitalAssistant: builder.mutation<
      CreateDigitalAssistantResponse,
      CreateDigitalAssistantRequest
    >({
      query: (data) => ({ url: BASE, method: 'POST', data }),
      transformResponse: normalizeOne<CreateDigitalAssistantResponse>,
      invalidatesTags: [{ type: 'AiAssistant', id: 'LIST' }],
    }),
    getDigitalAssistant: builder.query<GetDigitalAssistantResponse, string>({
      query: (id) => ({ url: `${BASE}/${seg(id)}`, method: 'GET' }),
      transformResponse: normalizeOne<GetDigitalAssistantResponse>,
      providesTags: (_result, _error, id) => [{ type: 'AiAssistant', id }],
    }),
    updateDigitalAssistant: builder.mutation<
      UpdateDigitalAssistantResponse,
      UpdateDigitalAssistantRequest
    >({
      query: ({ id, ...data }) => ({ url: `${BASE}/${seg(id)}`, method: 'PATCH', data }),
      transformResponse: normalizeOne<UpdateDigitalAssistantResponse>,
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiAssistant', id: arg.id },
        { type: 'AiAssistant', id: 'LIST' },
        { type: 'AiAssistantVersions', id: `LIST-${arg.id}` },
      ],
    }),
    duplicateDigitalAssistant: builder.mutation<
      DuplicateDigitalAssistantResponse,
      DuplicateDigitalAssistantRequest
    >({
      query: ({ id, ...data }) => ({ url: `${BASE}/${seg(id)}/duplicate`, method: 'POST', data }),
      transformResponse: normalizeOne<DuplicateDigitalAssistantResponse>,
      invalidatesTags: [{ type: 'AiAssistant', id: 'LIST' }],
    }),
    deleteDigitalAssistant: builder.mutation<DeleteDigitalAssistantResponse, string>({
      query: (id) => ({ url: `${BASE}/${seg(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'AiAssistant', id },
        { type: 'AiAssistant', id: 'LIST' },
      ],
    }),
    listDigitalAssistantVersions: builder.query<ListDigitalAssistantVersionsResponse, string>({
      query: (id) => ({ url: `${BASE}/${seg(id)}/versions`, method: 'GET' }),
      transformResponse: normalizeMany<ListDigitalAssistantVersionsResponse>,
      providesTags: (result, _error, id) =>
        result
          ? [
              ...result.data.map((version) => ({
                type: 'AiAssistantVersions' as const,
                id: version.id,
              })),
              { type: 'AiAssistantVersions' as const, id: `LIST-${id}` },
            ]
          : [{ type: 'AiAssistantVersions' as const, id: `LIST-${id}` }],
    }),
    getDigitalAssistantVersion: builder.query<
      GetDigitalAssistantVersionResponse,
      GetDigitalAssistantVersionRequest
    >({
      query: ({ id, versionNumber }) => ({
        url: `${BASE}/${seg(id)}/versions/${versionNumber}`,
        method: 'GET',
      }),
      transformResponse: normalizeOne<GetDigitalAssistantVersionResponse>,
      providesTags: (_result, _error, arg) => [
        { type: 'AiAssistantVersions', id: arg.versionNumber },
        { type: 'AiAssistantVersions', id: `LIST-${arg.id}` },
      ],
    }),
    restoreDigitalAssistantVersion: builder.mutation<
      RestoreDigitalAssistantVersionResponse,
      RestoreDigitalAssistantVersionRequest
    >({
      query: ({ id, versionNumber, changeNote }) => ({
        url: `${BASE}/${seg(id)}/versions/${versionNumber}/restore`,
        method: 'POST',
        data: { changeNote },
      }),
      transformResponse: normalizeOne<RestoreDigitalAssistantVersionResponse>,
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiAssistant', id: arg.id },
        { type: 'AiAssistant', id: 'LIST' },
        { type: 'AiAssistantVersions', id: `LIST-${arg.id}` },
        { type: 'AiAssistantVersions', id: arg.versionNumber },
      ],
    }),
    archiveDigitalAssistantVersion: builder.mutation<
      ArchiveDigitalAssistantVersionResponse,
      ArchiveDigitalAssistantVersionRequest
    >({
      query: ({ id, versionNumber }) => ({
        url: `${BASE}/${seg(id)}/versions/${versionNumber}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AiAssistantVersions', id: `LIST-${arg.id}` },
        { type: 'AiAssistantVersions', id: arg.versionNumber },
      ],
    }),

    // Executing an assistant changes no configuration, so these invalidate nothing.
    executeDigitalAssistant: builder.mutation<
      ExecuteDigitalAssistantResponse,
      ExecuteDigitalAssistantRequest
    >({
      query: (data) => ({ url: '/llm/digital-assistants-runtime/execute', method: 'POST', data }),
    }),
    executeListingPayload: builder.mutation<
      ExecuteListingPayloadResponse,
      ExecuteListingPayloadRequest
    >({
      query: (data) => ({
        url: '/llm/digital-assistants-runtime/execute-assistant',
        method: 'POST',
        data,
      }),
    }),
  }),
});

export const {
  useListDigitalAssistantsQuery,
  useCreateDigitalAssistantMutation,
  useGetDigitalAssistantQuery,
  useLazyGetDigitalAssistantQuery,
  useUpdateDigitalAssistantMutation,
  useDuplicateDigitalAssistantMutation,
  useDeleteDigitalAssistantMutation,
  useListDigitalAssistantVersionsQuery,
  useGetDigitalAssistantVersionQuery,
  useLazyGetDigitalAssistantVersionQuery,
  useRestoreDigitalAssistantVersionMutation,
  useArchiveDigitalAssistantVersionMutation,
  useExecuteDigitalAssistantMutation,
  useExecuteListingPayloadMutation,
} = digitalAssistantsApi;
